import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash, WarningCircle, Airplane } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Leave() {
  const [leaves, setLeaves] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ person_id: "", start_date: "", end_date: "", reason: "" });

  const load = async () => {
    const [l, c, p] = await Promise.all([api.get("/leave"), api.get("/leave/conflicts"), api.get("/personnel")]);
    setLeaves(l.data); setConflicts(c.data); setPersonnel(p.data);
  };
  useEffect(() => { load(); }, []);

  const personMap = Object.fromEntries(personnel.map(p => [p.id, p]));

  const save = async () => {
    if (!form.person_id || !form.start_date || !form.end_date) { toast.error("All fields required"); return; }
    try { await api.post("/leave", form); toast.success("Leave logged"); setOpen(false); setForm({ person_id: "", start_date: "", end_date: "", reason: "" }); load(); }
    catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete leave record?")) return;
    await api.delete(`/leave/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <PageHeader
        overline="Availability"
        title="Leave Calendar"
        subtitle="Track time-off across engineers and field techs. Conflicts with active project assignments are flagged automatically."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-leave-btn" className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-mono uppercase tracking-wider text-xs"><Plus size={14} className="mr-2"/> Log Leave</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-md">
              <DialogHeader><DialogTitle>Log Leave</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Person</Label>
                  <Select value={form.person_id} onValueChange={(v) => setForm({...form, person_id: v})}>
                    <SelectTrigger data-testid="leave-person" className="bg-slate-50 border-slate-300 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{personnel.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start</Label><Input data-testid="leave-start" type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                  <div><Label>End</Label><Input data-testid="leave-end" type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                </div>
                <div><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} className="bg-slate-50 border-slate-300 mt-1" placeholder="Annual leave, sick, training..."/></div>
              </div>
              <DialogFooter><Button data-testid="save-leave-btn" onClick={save} className="bg-[#FFC107] hover:bg-[#E5A906]">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Conflicts */}
      {conflicts.length > 0 && (
        <div data-testid="leave-conflicts" className="mb-6 border border-red-500/30 bg-red-500/[0.06] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><WarningCircle size={16} weight="fill" className="text-red-400" /><div className="overline text-red-300">{conflicts.length} assignment conflict{conflicts.length > 1 ? "s" : ""} — resolve before deployment</div></div>
          <div className="space-y-1">
            {conflicts.map((c, i) => (
              <div key={i} className="text-xs font-mono">
                <span className="text-red-300">{c.person_name}</span> on leave <span className="text-slate-500">{c.leave_start} → {c.leave_end}</span> but assigned to <span className="text-[#0F1E38]">{c.project_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left overline border-b border-slate-200">
              <th className="px-4 py-3">Person</th><th>Dates</th><th>Days</th><th>Reason</th><th></th>
            </tr>
          </thead>
          <tbody>
            {leaves.map((lv) => {
              const person = personMap[lv.person_id] || {};
              const days = Math.max(1, Math.round((new Date(lv.end_date) - new Date(lv.start_date)) / 86400000) + 1);
              return (
                <tr key={lv.id} data-testid={`leave-row-${lv.id}`} className="border-b border-slate-100">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Airplane size={14} className="text-[#0F1E38]" />
                      <div>
                        <div className="text-[#0F1E38]">{person.name || "—"}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{person.role_title}</div>
                      </div>
                    </div>
                  </td>
                  <td className="font-mono text-xs text-slate-700">{lv.start_date} → {lv.end_date}</td>
                  <td className="font-mono text-xs text-[#0F1E38]">{days}d</td>
                  <td className="text-slate-500 text-xs">{lv.reason || "—"}</td>
                  <td className="pr-4 text-right"><button data-testid={`leave-del-${lv.id}`} onClick={() => del(lv.id)} className="text-slate-500 hover:text-red-400" style={{transition:"color 0.15s ease"}}><Trash size={13}/></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {leaves.length === 0 && <div className="grid-bg py-16 text-center text-sm text-slate-500">No leave logged.</div>}
      </div>
    </div>
  );
}
